import { getRankIndex } from '../card';
import { RANK_ORDER_DEUCE_TO_SEVEN } from '../constants';
import { Label, Rank } from '../enums';
import { Card, Entry } from '../types';
import { getRankCounts, isFlush } from './helpers';

function isStraightLow(cards: readonly Card[]): boolean {
  if (cards.length !== 5) return false;

  const indices = cards.map((c) => getRankIndex(c.rank, RANK_ORDER_DEUCE_TO_SEVEN)).sort((a, b) => a - b);

  for (let i = 1; i < indices.length; i++) {
    if (indices[i] !== indices[i - 1] + 1) {
      return false;
    }
  }
  return true;
}

export function evaluateLowHand(cards: readonly Card[]): Entry {
  if (cards.length !== 5) {
    throw new Error(`Low hand requires exactly 5 cards, got ${cards.length}`);
  }

  const rankCounts = getRankCounts(cards);
  const flush = isFlush(cards);
  const straight = isStraightLow(cards);

  const counts = Array.from(rankCounts.entries()).sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return getRankIndex(a[0], RANK_ORDER_DEUCE_TO_SEVEN) - getRankIndex(b[0], RANK_ORDER_DEUCE_TO_SEVEN);
  });

  const lowRanks = cards
    .map((c) => c.rank)
    .sort((a, b) => getRankIndex(a, RANK_ORDER_DEUCE_TO_SEVEN) - getRankIndex(b, RANK_ORDER_DEUCE_TO_SEVEN));

  if (counts[0][1] === 4) {
    return {
      label: Label.FOUR_OF_A_KIND,
      index: buildLowIndex(8, lowRanks),
    };
  }

  if (counts[0][1] === 3 && counts[1][1] === 2) {
    return {
      label: Label.FULL_HOUSE,
      index: buildLowIndex(7, lowRanks),
    };
  }

  if (counts[0][1] === 3) {
    return {
      label: Label.THREE_OF_A_KIND,
      index: buildLowIndex(6, lowRanks),
    };
  }

  if (counts[0][1] === 2 && counts[1][1] === 2) {
    return {
      label: Label.TWO_PAIR,
      index: buildLowIndex(5, lowRanks),
    };
  }

  if (counts[0][1] === 2) {
    return {
      label: Label.ONE_PAIR,
      index: buildLowIndex(4, lowRanks),
    };
  }

  if (straight && flush) {
    return {
      label: Label.STRAIGHT_FLUSH,
      index: buildLowIndex(3, lowRanks),
    };
  }

  if (flush) {
    return {
      label: Label.FLUSH,
      index: buildLowIndex(2, lowRanks),
    };
  }

  if (straight) {
    return {
      label: Label.STRAIGHT,
      index: buildLowIndex(1, lowRanks),
    };
  }

  return {
    label: Label.HIGH_CARD,
    index: buildLowIndex(0, lowRanks),
  };
}

function buildLowIndex(handTypePenalty: number, ranks: Rank[]): number {
  let index = handTypePenalty * 1000000;
  for (let i = 0; i < ranks.length; i++) {
    index = index * 13 + getRankIndex(ranks[i], RANK_ORDER_DEUCE_TO_SEVEN);
  }
  return -index;
}
